import { Plus } from 'lucide-react'
import { Link, useNavigate } from 'react-router'
import { KpiTile } from '@/components/redesign/bits'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState, ErrorState } from '@/components/ui/states'
import { PageTitle } from '@/app/layout'
import { useMe } from '@/features/auth/api'
import { cn } from '@/lib/cn'
import { formatDate, formatDateTime, numberFmt } from '@/lib/format'
import { useAnnouncements } from './api'
import { chipFor } from './format'

/**
 * News in the app (Engage → Announcements): service announcements and product news, each
 * notifying every open account once. In-app only since 2026-10-23; emails go through Marketing.
 */
export function AnnouncementsPage() {
  const list = useAnnouncements()
  const me = useMe()
  const navigate = useNavigate()
  const canWrite = me.data?.role === 'owner' || me.data?.role === 'admin'

  return (
    <>
      <PageTitle
        title="Announcements"
        description="Publish news in the app and notify customers once. To email customers, use Marketing."
        actions={
          canWrite && (
            <Link to="/announcements/new" className="btn primary">
              <Plus aria-hidden="true" />
              New announcement
            </Link>
          )
        }
      />
      {list.isPending ? (
        <Skeleton className="h-64 rounded-[14px]" />
      ) : list.isError ? (
        <div className="card">
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        </div>
      ) : (
        <>
          <div className="kpis">
            <KpiTile
              label="In-app audience"
              value={numberFmt.format(list.data.meta.in_app_audience)}
              compare="people notified when published"
              hint="Every eligible customer, regardless of marketing email consent. Announcements never send email."
            />
          </div>
          {list.data.data.length === 0 ? (
            <div className="card">
              <EmptyState
                title="No announcements yet"
                description="Save a draft, review it, then publish. Customers get an in-app notification; no email is sent."
              />
            </div>
          ) : (
            <div className="tw">
              <table className="t" style={{ minWidth: 720 }}>
                <caption className="sr-only">Announcements</caption>
                <thead>
                  <tr>
                    <th>Subject</th>
                    <th>Status</th>
                    <th className="r">Notified</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {list.data.data.map((a) => (
                    <tr key={a.id} className="click" onClick={() => void navigate(`/announcements/${a.id}`)}>
                      <td>
                        <Link
                          to={`/announcements/${a.id}`}
                          style={{ fontWeight: 600, color: 'var(--ink)' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {a.subject}
                        </Link>
                        <small>{list.data.meta.kinds[a.kind].label}</small>
                      </td>
                      <td>
                        <span
                          className={cn(
                            'chip',
                            chipFor(a.status, a.published_at !== null, Boolean(a.scheduled_at)).tone,
                          )}
                        >
                          {chipFor(a.status, a.published_at !== null, Boolean(a.scheduled_at)).label}
                        </span>
                      </td>
                      <td className="r num">
                        {a.status !== 'draft' ? numberFmt.format(a.in_app_recipients) : '—'}
                        {a.email_delivery && a.progress && (
                          <small>Emailed {numberFmt.format(a.progress.sent)} (before in-app only)</small>
                        )}
                      </td>
                      <td className="m" style={{ whiteSpace: 'nowrap' }}>
                        {a.sent_at
                          ? formatDate(a.sent_at)
                          : a.scheduled_at
                            ? formatDateTime(a.scheduled_at)
                            : a.updated_at
                              ? formatDate(a.updated_at)
                              : '—'}
                        {(a.sent_by ?? a.scheduled_by ?? a.updated_by) && (
                          <small>{a.sent_by ?? a.scheduled_by ?? a.updated_by}</small>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </>
  )
}
